<script lang="ts">
	import { Img, Avatar, Badge } from 'flowbite-svelte';
	import { tokenData, unreadNotificationCount } from '../Auth';

	import RoutedLink from './RoutedLink.svelte';
	import { push } from '../abstract-spa-router';
	import { t } from '../i18n';

	/**
	 * Hand off to the dedicated sign-out route, which revokes the session
	 * server-side before clearing local state. A full page load because
	 * `/logout` lives in the public app, not the admin one.
	 */
	const fnOnSignOut = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		window.location.href = '/logout';
	};

	interface Props {
		/**
		 * Strips the card down to a single avatar row — no AbstractID banner, no
		 * links, no notification badge. This is the sidebar form: at 250px there
		 * is only room for the identity itself.
		 */
		minimal?: boolean;
		/** Whether to use a compact (smaller) layout. */
		compact?: boolean;
		/** Whether to show Account / Sign Out links. */
		showLinks?: boolean;
		/** Whether the card is clickable. */
		clickable?: boolean;
		/** Callback fired when the card is clicked. */
		onclick?: () => void;
	}

	let { minimal = false, compact = false, showLinks = true, clickable = true, onclick }: Props = $props();

	/** Minimum card height. Minimal is a single row, so it only needs the avatar. */
	let minimumCardHeight: number = $derived(minimal ? 20 : compact ? 80 : 100);

	/** Whether a profile picture is available. */
	let hasAvatar = $derived(!!$tokenData?.profilePictureURL);
	/** Absolute avatar URL from the JWT (presigned S3 URL or local). */
	let avatarUrl = $derived($tokenData?.profilePictureURL || '');

	/** Display name — falls back to email when no first/last name is set. */
	let displayName = $derived(
		$tokenData?.firstName || $tokenData?.lastName
			? `${$tokenData?.firstName ?? ''} ${$tokenData?.lastName ?? ''}`.trim()
			: ''
	);
	/** Only show the email sub-line when we already have a real name above it. */
	let showEmailLine = $derived(!!displayName);
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
	class="w-[270px] max-w-[100%] {clickable
		? 'cursor-pointer hover:ring'
		: ''} relative z-0 mt-8 rounded-lg bg-[length:128px_128px] p-2 outline outline-1 outline-offset-2 outline-zinc-300 dark:outline-zinc-700"
	style="min-height: {minimumCardHeight}px"
	onclick={() => onclick?.()}
>
	{#if !minimal}
		<Img
			class="{compact ? 'max-h-[12px]' : 'max-h-[16px]'} mx-auto text-center opacity-90 dark:invert"
			src="/AbstractID-Logo.png"
		/>
	{/if}

	<div
		class="flex {showEmailLine ? 'items-start' : 'items-center'} space-x-2 align-top {minimal
			? 'text-xs'
			: compact
				? 'mt-1'
				: 'mt-2'} zindex-10 relative {minimal ? '' : '-mb-1'}"
	>
		<Avatar
			size={minimal ? 'sm' : 'md'}
			class="shrink-0 rounded-full {minimal ? 'text-xs' : ''}"
			src={hasAvatar ? avatarUrl : ''}
		/>
		<!-- min-w-0 lets this flex child shrink below its content width, which is what
		     makes `truncate` (overflow-hidden + text-ellipsis) actually clip instead of
		     letting a long name or email push past the card edge. -->
		<div class="w-full min-w-0 {minimal ? '' : 'pb-1'} font-normal text-gray-900 dark:text-white">
			<div class="truncate font-semibold" title={displayName || $tokenData?.email}>
				{displayName || $tokenData?.email}
			</div>
			{#if showEmailLine}
				<div class="truncate text-xs dark:text-gray-300" title={$tokenData?.email}>{$tokenData?.email}</div>
			{/if}
		</div>
		{#if compact || minimal}
			{#if $unreadNotificationCount > 0}
				<!-- Both dots share one positioned wrapper so the ping stays concentric with
				     the dot — as direct flex children they picked up the row's space-x-2
				     margin unevenly (Tailwind v4 skips the last child) and drifted apart. -->
				<span class="absolute top-0 right-0 flex h-2 w-2">
					<span class="absolute inset-0 animate-ping rounded-full bg-red-500 opacity-75"></span>
					<span class="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
				</span>
			{/if}
		{/if}
	</div>

	{#if !compact && !minimal}
		<Badge
			color={$unreadNotificationCount > 0 ? 'red' : 'gray'}
			class="absolute top-2 left-2 h-4 w-10 cursor-pointer !p-0.5 !px-1 text-[12px] leading-[8px] {$unreadNotificationCount >
			0
				? 'animate-pulse !bg-red-500/40'
				: ''}"
			onclick={(e: MouseEvent) => {
				e.stopPropagation();
				push('/Settings/Account/Notifications');
			}}
		>
			<i class="fa-solid {$unreadNotificationCount > 0 ? 'fa-bell' : 'fa-bell'} mr-0.5 inline text-[10px]"></i>
			{$unreadNotificationCount}
		</Badge>
	{/if}

	{#if showLinks && !minimal}
		<div class="mt-4 text-center text-sm whitespace-nowrap text-gray-800 dark:text-gray-400">
			<div>
				<RoutedLink
					class="mr-2 font-bold text-gray-700 hover:text-black hover:underline dark:text-gray-400 dark:hover:text-white"
					href="/Settings/Account">{$t("Account")}</RoutedLink
				>
				<!-- svelte-ignore a11y_no_static_element_interactions -->
				<!-- svelte-ignore a11y_click_events_have_key_events -->
				<a
					class="cursor-pointer font-bold text-gray-700 hover:text-black hover:underline dark:text-gray-400 dark:hover:text-white"
					id="sign-out"
					onclick={fnOnSignOut}
				>
					{$t("Sign Out")}
				</a>
			</div>
		</div>
	{/if}
	<div
		class="absolute top-0 left-0 h-full w-full rounded-lg bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-700"
		style="z-index: -1"
	></div>
	<div
		class="absolute top-0 left-0 h-full w-full rounded-lg bg-repeat opacity-30 dark:invert"
		style="z-index: -1;background-image: url('/AbstractID-Tile-Dark.png')"
	></div>
</div>
